/**
 * Rutas del backend, en un solo sitio.
 *
 * Es el contrato que el panel espera. Está centralizado a propósito: cuando una
 * ruta cambie, la discrepancia se corrige aquí y no repartida por veinte
 * componentes. La forma exacta de cada petición y respuesta está documentada en
 * `apps/panel/README.md`, con una columna que dice si el backend ya la sirve.
 */
export const ROUTES = {
  health: '/health',

  // Módulo 9 — identidad
  login: '/auth/login',
  mfaVerify: '/auth/mfa/verify',
  refresh: '/auth/refresh',
  logout: '/auth/logout',
  me: '/auth/me',

  // Módulo 0 — jerarquía
  hierarchyTree: '/hierarchy/tree',
  clients: '/clients',
  client: (id: string) => `/clients/${id}`,
  branches: '/branches',
  branch: (id: string) => `/branches/${id}`,
  nodes: '/nodes',
  node: (id: string) => `/nodes/${id}`,
  nodeInventory: (id: string) => `/nodes/${id}/inventory`,

  /**
   * Serie histórica de salud de un nodo (Módulo 7).
   *
   * **Todavía no existe en el backend**: no hay entidad que persista las
   * muestras del latido, solo la última en Redis. La ficha lo pide, y si
   * responde 404 cae a la serie que el propio panel va acumulando del canal en
   * vivo, que al menos cubre el rato que lleve abierta la pantalla.
   */
  nodeHealthSeries: (id: string) => `/nodes/${id}/health`,

  // Módulo 2 — enrollment. Todas existen.
  /** `POST` emite; `GET` lista los códigos y tokens vivos del tenant (§11). */
  enrollmentTokens: '/enrollment/tokens',
  /**
   * `DELETE` revoca uno: deja de servir en el mismo instante. El identificador
   * sale del listado — el secreto no se puede recuperar ni hace falta.
   */
  enrollmentToken: (id: string) => `/enrollment/tokens/${id}`,
  enrollmentCa: '/enrollment/ca',
  /**
   * ¿Está libre este código manual? Lleva sesión a propósito: público sería un
   * oráculo que responde igual para códigos de otros inquilinos (plan de
   * enrolamiento por código, §6).
   */
  enrollmentCodeCheck: '/enrollment/code/check',
  /**
   * Traduce el código tecleado en la caja de destino a su comando de
   * instalación. **Pública**: la caja del cliente no tiene sesión del panel ni
   * debe tenerla, y ahí está el sentido entero del plan (§1).
   *
   * `POST` y el código en el cuerpo, no en la URL: un
   * `GET /enrollment/code/4K7M9Q` dejaría el secreto en el log de acceso del
   * OCC, en el del proxy inverso y en cualquier caché por el camino.
   */
  enrollmentCodeResolve: '/enrollment/code/resolve',
  /**
   * ¿Ya entró alguna máquina con este código? Alimenta el indicador en vivo de
   * `/i` (§7, paso 6). Pública y con los mismos límites que `resolve`: más
   * barata sería un segundo oráculo, y el más cómodo de todos porque la página
   * la llama en bucle.
   */
  enrollmentCodeStatus: '/enrollment/code/status',

  // Catálogo de acciones (P3): la misma puerta que usa el servidor MCP.
  action: (name: string) => `/actions/${name}`,

  /**
   * Cierre de una sesión de soporte desde el panel. **Ya existe**, con el JWT del
   * operador y no con el token interno del gateway.
   *
   * Ojo al permiso, porque no es uno solo: cerrar la propia sesión pide
   * `support_technician` sobre el nodo, y cerrar **la de otro operador** pide
   * `tenant_admin` — interrumpe el trabajo de un compañero y corta su grabación.
   * Un técnico recibe 403 con ese motivo, y una sesión ya cerrada responde 404.
   */
  closeSession: (id: string) => `/sessions/${id}/close`,

  /**
   * Staging para subir archivos desde el disco del operador (Módulo 6.3).
   *
   * **Ya existe.** Devuelve `{stagingKey, uploadUrl, method, headers, sizeBytes,
   * expiresAt}`: el `PUT` va al almacén de objetos, no al backend, y después
   * `files.upload` reclama esa clave.
   *
   * El tamaño declarado viaja **dentro de la firma**, así que el `PUT` tiene que
   * subir exactamente `sizeBytes` bytes: uno más y el almacén responde 403 por
   * firma inválida, no por tope excedido. Pedir el vale exige
   * `support_technician` a nivel de tenant.
   */
  fileStaging: '/files/staging',

  // Módulo 10 — bitácora. **Pendiente**: `AuditService` escribe pero no hay
  // controlador que consulte.
  audit: '/audit',

  // Módulo 9 — usuarios y permisos. **Pendiente** por completo.
  users: '/users',
  user: (id: string) => `/users/${id}`,
  userPermissions: (id: string) => `/users/${id}/permissions`,
  userPermission: (id: string, scopeType: string, scopeId: string) =>
    `/users/${id}/permissions/${scopeType}/${scopeId}`,

  /**
   * Módulo 8 §12 — branding del tenant (RF-21). **Ya existe.**
   *
   * `GET` lo lee cualquiera con sesión y responde **404 cuando nadie lo
   * configuró**, que es lo que el proveedor de branding ya interpreta como "usa el
   * tema por omisión". `PUT` pide `tenant_admin` y valida más de lo que el panel
   * validaba: los colores van por lista blanca —lo que se guarda acaba dentro de
   * una propiedad CSS— y un logo en `data:` tiene que ser de verdad el tipo que
   * dice, sin `<script>` si es SVG y por debajo de 256 KiB.
   */
  branding: '/tenants/current/branding',

  // Módulo 7 — alertas. **Ya existe** (`GET /alerts`, `POST /alerts/:id/acknowledge`).
  alerts: '/alerts',
  acknowledgeAlert: (id: string) => `/alerts/${id}/acknowledge`,

  /**
   * Interruptores por nodo, en la ficha del nodo (M15 §4, ampliado a
   * consumo): `node_offline`, `cpu_percent`, `memory_percent`. Por REST y no
   * por el catálogo — mismo motivo que las reglas de alerta: no es algo que un
   * nodo ejecute.
   */
  nodeToggle: (nodeId: string, metric: string) => `/alerts/node-notify/${nodeId}/${metric}`,

  /**
   * Procesos y contenedores vigilados de un nodo (M16): lista y alta/pausa por
   * `PUT`, baja por `DELETE`. Por REST y no por el catálogo, por el mismo motivo
   * que los interruptores.
   */
  nodeWatches: (nodeId: string) => `/alerts/node-watches/${nodeId}`,
  nodeWatch: (nodeId: string, ruleId: string) => `/alerts/node-watches/${nodeId}/${ruleId}`,

  /**
   * Módulo 12 — canales de notificación (Slack, WhatsApp, correo, Discord).
   * **Ya existe.** Administración por REST, igual que los plugins y las reglas
   * de alerta: crear un canal no es algo que un nodo ejecute, y publicarlo como
   * herramienta MCP le daría a un agente de lenguaje natural la capacidad de
   * redirigir las alertas del tenant a otro sitio (M15 §3).
   */
  notificationChannels: '/notifications/channels',
  notificationChannel: (id: string) => `/notifications/channels/${id}`,
  testNotificationChannel: (id: string) => `/notifications/channels/${id}/test`,

  /**
   * Módulo 6.1 — plugins (biblioteca de scripts parametrizados). **Ya existe.**
   *
   * La administración (crear, editar, habilitar por cliente) es REST, igual
   * que las reglas de alerta: no es algo que un nodo ejecute. **Correr** un
   * plugin sí pasa por el catálogo — es `script.run` con `libraryId` — así
   * que no tiene ruta propia aquí.
   */
  plugins: '/plugins',
  plugin: (id: string) => `/plugins/${id}`,
  pluginsRunnable: '/plugins/runnable',
  pluginEnablements: (id: string) => `/plugins/${id}/clients`,
  pluginEnablement: (id: string, clientId: string) => `/plugins/${id}/clients/${clientId}`,

  /**
   * Módulo 14 — programador de tareas. **Ya existe.** Por REST y no por el
   * catálogo de acciones: crear o disparar una tarea es administración de
   * flota, no algo que un nodo ejecute — mismo criterio que reglas de alerta
   * y plugins.
   */
  scheduledTasks: '/scheduled-tasks',
  scheduledTask: (id: string) => `/scheduled-tasks/${id}`,
  scheduledTaskRuns: (id: string) => `/scheduled-tasks/${id}/runs`,
  scheduledTaskRunNow: (id: string) => `/scheduled-tasks/${id}/run-now`,

  /**
   * Credenciales personales MCP (RF-13, autoservicio). **Ya existe.**
   *
   * `POST` devuelve el token completo una sola vez; `GET` lista sin el secreto;
   * `DELETE` revoca (nunca borra la fila).
   */
  mcpCredentials: '/mcp/credentials',
  mcpCredential: (id: string) => `/mcp/credentials/${id}`,

  // Estado en vivo
  events: '/ws/events',
} as const;

/** Subprotocolo del WebSocket de estado en vivo del panel. */
export const PANEL_WS_PROTOCOL = 'occ.panel.v1';

/**
 * Subprotocolo del puente de terminal. Es el mismo en los tres saltos
 * (navegador → gateway Web-SSH → broker), y está copiado de
 * `apps/webssh-gateway/src/bridge.ts`: si alguno de los dos cambia sin el otro,
 * el `handleProtocols` del gateway rechaza el upgrade y se ve enseguida.
 */
export const TERMINAL_WS_PROTOCOL = 'occ.terminal.v1';
